// ═══════════════════════════════════════════════════════════════
// NEW MEMBERS SIDEBAR — Collapsible right panel
// Shows members whose startDate is within the last 90 days
// Sort: First-time members on top, then renewals — each group A→Z by lastName
// Path: src/components/features/NewDonorsSidebar.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useMemo, useState } from "react";
import {
  UserPlus,
  X,
  ChevronRight,
  Calendar,
  Building2,
  Sparkles,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";

// ── Constants ──────────────────────────────────────────────────
const NEW_MEMBER_DAYS = 90;

const TIME_WINDOWS = [
  { label: "30d", days: 30 },
  { label: "60d", days: 60 },
  { label: "90d", days: 90 },
];

// ── Utilities ──────────────────────────────────────────────────
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

const daysBetween = (date1, date2) => {
  return Math.floor((date2 - date1) / (1000 * 60 * 60 * 24));
};

// ── Compute new donors with enriched data ─────────────────────
const computeNewMembers = (members, maxDays) => {
  const now = new Date();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - maxDays);

  return (
    members
      .map((m) => {
        const startDate = parseDate(m.startDate);
        if (!startDate || startDate < cutoff) return null;
        const daysAgo = daysBetween(startDate, now);
        const isFirstTime = (() => {
          const memberSince = parseDate(m.memberSince);
          if (!memberSince || !startDate) return false;
          // If memberSince and startDate are within 60 days, likely first-time
          return Math.abs(daysBetween(memberSince, startDate)) <= 60;
        })();
        return { ...m, startDateParsed: startDate, daysAgo, isFirstTime };
      })
      .filter(Boolean)
      // ── SORT: First-timers on top, then renewals — each group A→Z by lastName ──
      .sort((a, b) => {
        // First-timers float to top
        if (a.isFirstTime !== b.isFirstTime) return b.isFirstTime ? 1 : -1;
        // Then alphabetical by last name
        return (a.lastName || "").localeCompare(b.lastName || "");
      })
  );
};

// ── Format daysAgo for display ─────────────────────────────────
const formatDaysAgo = (daysAgo) => {
  if (daysAgo === 0) return "Today";
  if (daysAgo === 1) return "Yesterday";
  return `${daysAgo} days ago`;
};

// ═══════════════════════════════════════════════════════════════
// MAIN SIDEBAR COMPONENT
// ═══════════════════════════════════════════════════════════════
export const NewDonorsSidebar = ({ isOpen, onClose, onMemberClick }) => {
  const { palette, members } = useApp();
  const [selectedWindow, setSelectedWindow] = useState(90);

  // Compute for the max window (90 days) so we can show counts for all tabs
  const allNewMembers = useMemo(
    () => computeNewMembers(members, 90),
    [members]
  );

  // Filter to selected window
  const filteredMembers = useMemo(
    () => allNewMembers.filter((m) => m.daysAgo <= selectedWindow),
    [allNewMembers, selectedWindow]
  );

  // Counts per window for the tab badges
  const countByWindow = useMemo(() => {
    const counts = {};
    TIME_WINDOWS.forEach((w) => {
      counts[w.days] = allNewMembers.filter((m) => m.daysAgo <= w.days).length;
    });
    return counts;
  }, [allNewMembers]);

  // Count first-timers vs renewals in current window
  const firstTimerCount = filteredMembers.filter((m) => m.isFirstTime).length;
  const renewalCount = filteredMembers.length - firstTimerCount;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 998,
          background: "rgba(0,0,0,0.15)",
        }}
      />

      {/* Sidebar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 320,
          zIndex: 999,
          background: palette.cardBg,
          borderLeft: `1px solid ${palette.cardBorder}`,
          boxShadow: "-4px 0 24px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          fontFamily: FONT_FAMILY,
          animation: "newMemberSlideIn 0.2s ease-out",
        }}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div
          style={{
            padding: "16px 18px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: `linear-gradient(135deg, ${
              palette.accent1
            }15, ${palette.accent1}05)`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: palette.accent1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <UserPlus size={ICON_SIZES.md} style={{ color: "#fff" }} />
            </div>
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: palette.text1,
                }}
              >
                New Members
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: palette.text3,
                }}
              >
                {filteredMembers.length} member
                {filteredMembers.length !== 1 ? "s" : ""} in last{" "}
                {selectedWindow} days
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "#6b728015",
              border: "none",
              cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "#6b728030")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "#6b728015")
            }
          >
            <X size={ICON_SIZES.sm} style={{ color: palette.text2 }} />
          </button>
        </div>

        {/* ── Time Window Tabs ───────────────────────────────── */}
        <div
          style={{
            display: "flex",
            padding: "8px 12px",
            gap: 6,
            borderBottom: `1px solid ${palette.cardBorder}`,
            background: palette.toolbarBg,
          }}
        >
          {TIME_WINDOWS.map((w) => {
            const isActive = selectedWindow === w.days;
            const count = countByWindow[w.days];
            return (
              <button
                key={w.days}
                onClick={() => setSelectedWindow(w.days)}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  padding: "8px 4px",
                  borderRadius: 8,
                  border: isActive
                    ? `2px solid ${palette.accent1}`
                    : "2px solid transparent",
                  background: isActive
                    ? `${palette.accent1}10`
                    : "transparent",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive
                      ? palette.accent1
                      : palette.text2,
                  }}
                >
                  {w.label}
                </span>
                <span
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: isActive
                      ? palette.accent1
                      : palette.text1,
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Section Summary (first-timers vs renewals) ──────── */}
        {filteredMembers.length > 0 && (
          <div
            style={{
              padding: "8px 18px",
              borderBottom: `1px solid ${palette.cardBorder}`,
              display: "flex",
              gap: 12,
              fontSize: 11,
              color: palette.text3,
            }}
          >
            {firstTimerCount > 0 && (
              <span>
                <Sparkles
                  size={ICON_SIZES.xs}
                  style={{
                    color: "#f59e0b",
                    verticalAlign: "middle",
                    marginRight: 3,
                  }}
                />
                {firstTimerCount} first-time
              </span>
            )}
            {renewalCount > 0 && (
              <span>
                <Calendar
                  size={ICON_SIZES.xs}
                  style={{
                    color: palette.text3,
                    verticalAlign: "middle",
                    marginRight: 3,
                  }}
                />
                {renewalCount} renewal{renewalCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}

        {/* ── Member List ────────────────────────────────────── */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "6px 0",
          }}
        >
          {filteredMembers.length === 0 ? (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: palette.text3,
              }}
            >
              <UserPlus
                size={ICON_SIZES.xxl}
                style={{
                  color: palette.text3,
                  opacity: 0.4,
                  marginBottom: 8,
                }}
              />
              <div style={{ fontSize: 13, fontWeight: 500 }}>
                No new donors
              </div>
              <div style={{ fontSize: 11, marginTop: 4 }}>
                in the last {selectedWindow} days
              </div>
            </div>
          ) : (
            filteredMembers.map((m) => (
              <button
                key={m.id}
                onClick={() => {
                  onMemberClick?.(m);
                  onClose();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "10px 18px",
                  border: "none",
                  borderBottom: `1px solid ${
                    palette.cardBorder
                  }20`,
                  background: "transparent",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background 0.12s",
                  fontFamily: FONT_FAMILY,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = `${
                    palette.accent1
                  }08`)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                {/* Avatar */}
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: m.isFirstTime
                      ? `linear-gradient(135deg, #f59e0b, #d97706)`
                      : `linear-gradient(135deg, ${
                          palette.accent1
                        }30, ${palette.accent1}15)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: 13,
                    fontWeight: 700,
                    color: m.isFirstTime
                      ? "#fff"
                      : palette.accent1,
                  }}
                >
                  {(m.firstName || "?")[0]}
                  {(m.lastName || "?")[0]}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: palette.text1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {m.lastName || ""}, {m.firstName || ""}
                    {m.credentials ? `, ${m.credentials}` : ""}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: palette.text2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {m.organization && (
                      <>
                        <Building2 size={ICON_SIZES.xs} style={{ flexShrink: 0 }} />
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {m.organization}
                        </span>
                      </>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: palette.text3,
                      marginTop: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {formatDaysAgo(m.daysAgo)}
                    {m.isFirstTime && (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "#f59e0b",
                          background: "#fef3c720",
                          padding: "0 5px",
                          borderRadius: 3,
                          marginLeft: 4,
                        }}
                      >
                        NEW
                      </span>
                    )}
                  </div>
                </div>

                <ChevronRight
                  size={ICON_SIZES.sm}
                  style={{
                    color: palette.text3,
                    flexShrink: 0,
                  }}
                />
              </button>
            ))
          )}
        </div>

        {/* ── Footer hint ────────────────────────────────────── */}
        {filteredMembers.length > 0 && (
          <div
            style={{
              padding: "10px 18px",
              borderTop: `1px solid ${palette.cardBorder}`,
              fontSize: 10,
              color: palette.text3,
              textAlign: "center",
            }}
          >
            Click any member to view their full profile
          </div>
        )}
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes newMemberSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
// TOGGLE BUTTON — Used in ZoneOneHeader rightContent
// Styled for gradient background (Zone 1) visibility
// ═══════════════════════════════════════════════════════════════
export const NewDonorsToggleButton = ({ onClick, memberCount }) => {
  const { palette } = useApp();

  if (memberCount === 0) return null;

  return (
    <button
      onClick={onClick}
      title={`${memberCount} new donor${
        memberCount !== 1 ? "s" : ""
      } in last 90 days`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.25)",
        background: "rgba(255,255,255,0.15)",
        color: "#ffffff",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: FONT_FAMILY,
        cursor: "pointer",
        transition: "all 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.25)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.15)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
      }}
    >
      <UserPlus size={ICON_SIZES.sm} />
      <span>New</span>
      <span
        style={{
          background: "#ffffff",
          color: palette.accent1,
          fontSize: 10,
          fontWeight: 700,
          padding: "1px 6px",
          borderRadius: 10,
          minWidth: 18,
          textAlign: "center",
        }}
      >
        {memberCount}
      </span>
    </button>
  );
};
